import React, { useState, useEffect } from 'react';
import './DashboardSchoolTeacher.css';
import '../../pages/PasswordVerification/PasswordVerification.css';
import { supabase } from '../../lib/supabase';
import { logUserActivity } from '../../lib/activityLogger';
import { UserIdGenerator } from '../../lib/userIdGenerator';
import NotificationModal from '../NotificationModal/NotificationModal';
import * as XLSX from 'xlsx';

// Helper function to convert database errors to user-friendly messages
const getFriendlyErrorMessage = (error, operation = 'operation') => {
  if (!error) return 'An unexpected error occurred.';

  const errorMessage = error.message || String(error);
  const errorCode = error.code || '';

  // Duplicate email error
  if (
    errorMessage.includes('users_school_id_email_key') ||
    (errorMessage.includes('duplicate key') && errorMessage.includes('email'))
  ) {
    return 'This email address is already registered for a student or teacher in this school. Please use a different email address.';
  }

  // Duplicate user_id error
  if (
    errorMessage.includes('users_user_id_key') ||
    (errorMessage.includes('duplicate key') && errorMessage.includes('user_id'))
  ) {
    return 'A user with this ID already exists. Please try again.';
  }

  // Foreign key constraint errors
  if (errorMessage.includes('foreign key constraint') || errorMessage.includes('violates foreign key')) {
    if (errorMessage.includes('school_id')) {
      return 'Invalid school selected. Please refresh the page and try again.';
    }
    if (errorMessage.includes('grade_id')) {
      return 'Invalid grade selected. Please select a valid grade.';
    }
    if (errorMessage.includes('grade_section_id')) {
      return 'Invalid grade section selected. Please select a valid section.';
    }
    return 'Invalid data provided. Please check your selections and try again.';
  }

  // Not null constraint errors
  if (errorMessage.includes('null value') && errorMessage.includes('violates not-null constraint')) {
    return 'Please fill in all required fields.';
  }

  // Network/connection errors
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorCode === 'PGRST116') {
    return 'Network error. Please check your internet connection and try again.';
  }

  // Return original message if no specific pattern matches
  return errorMessage;
};

const NoDataState = ({ type, icon, message, actionText, onAction }) => (
  <div className="school-teacher-no-data-state">
    <div className="school-teacher-no-data-icon">{icon}</div>
    <h3>No {type} Found</h3>
    <p>{message}</p>
    {onAction && (
      <button className="school-teacher-add-first-btn" onClick={onAction}>{actionText}</button>
    )}
  </div>
);

const DashboardSchoolTeacher = ({ teachers, subjects, grades, gradeSections, dataLoading, onTeacherAdded, readOnly = false }) => {
  const [showAddTeacherModal, setShowAddTeacherModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState('alphabet');
  const [filterValue, setFilterValue] = useState('');
  const [filterGradeId, setFilterGradeId] = useState('');
  const [filterGradeSectionId, setFilterGradeSectionId] = useState('');
  const [filterRole, setFilterRole] = useState('');

  const teacherRoles = [
    { value: '', label: 'No Role' },
    { value: 'curriculum_coordinator', label: 'Curriculum Coordinator' },
    { value: 'head_of_department', label: 'Head of Department' },
    { value: 'coordinator', label: 'Coordinator' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'assistant', label: 'Assistant' }
  ];

  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });
  const [teacherFormData, setTeacherFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    teacher_role: '',
    subject: '',
    language: '',
    grade: '',
    is_active: true,
    password: ''
  });
  const [newPassword, setNewPassword] = useState(''); // Separate state for password editing
  const [selectedGrades, setSelectedGrades] = useState(new Set()); // Stores keys like "grade_id" or "grade_id:section_id"
  const [addMode, setAddMode] = useState('single'); // 'single' or 'excel'
  const [excelFile, setExcelFile] = useState(null);
  const [isExcelUploading, setIsExcelUploading] = useState(false);
  const [excelSelectedGrades, setExcelSelectedGrades] = useState(new Set()); // For Excel upload grade selection
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialSelectedGrades, setInitialSelectedGrades] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showUploadResults, setShowUploadResults] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTeachers, setSelectedTeachers] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showDeleteByExcelModal, setShowDeleteByExcelModal] = useState(false);
  const [deleteExcelFile, setDeleteExcelFile] = useState(null);
  const [isDeleteExcelUploading, setIsDeleteExcelUploading] = useState(false);
  const [deleteExcelDragging, setDeleteExcelDragging] = useState(false);
  const [showDeleteExcelResults, setShowDeleteExcelResults] = useState(false);
  const [deleteExcelResults, setDeleteExcelResults] = useState(null);

  const TEACHERS_PER_PAGE = 100;
  const [showMaxLimitError, setShowMaxLimitError] = useState(false);
  const [maxLimitError, setMaxLimitError] = useState({ type: '', current: 0, max: 0 });
  const [activeTeachersCount, setActiveTeachersCount] = useState(0);
  const [maxTeachers, setMaxTeachers] = useState(null);

  // If the dashboard enters read-only mode, force-disable any destructive UX paths.
  useEffect(() => {
    if (!readOnly) return;
    setSelectionMode(false);
    setSelectedTeachers(new Set());
    setShowAddTeacherModal(false);
    setShowDetailsModal(false);
    setIsEditing(false);
  }, [readOnly]);

  // Fetch and update active teachers count and max limit
  useEffect(() => {
    const fetchCounts = async () => {
      const [currentCount, maxLimit] = await Promise.all([
        getActiveTeachersCount(),
        getSchoolMaxTeachers()
      ]);
      setActiveTeachersCount(currentCount);
      setMaxTeachers(maxLimit);
    };
    fetchCounts();
  }, [teachers]); // Re-fetch when teachers list changes

  // Set initial form data when modal opens
  useEffect(() => {
    if (showAddTeacherModal) {
      setInitialFormData(JSON.parse(JSON.stringify(teacherFormData)));
      setInitialSelectedGrades(new Set(selectedGrades));
    } else {
      setInitialFormData(null);
      setInitialSelectedGrades(null);
    }
  }, [showAddTeacherModal]);

  // Helper function to get school's max_teachers limit
  const getSchoolMaxTeachers = async () => {
    try {
      const schoolId = localStorage.getItem('school_id');
      if (!schoolId) return null;

      const { data, error } = await supabase
        .from('schools')
        .select('max_teachers')
        .eq('id', schoolId)
        .single();

      if (error) {return null;
      }

      return data?.max_teachers ?? null;
    } catch (err) {return null;
    }
  };

  // Helper function to count active teachers for the school
  const getActiveTeachersCount = async () => {
    try {
      const schoolId = localStorage.getItem('school_id');
      if (!schoolId) return 0;

      const { count, error } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('school_id', schoolId)
        .eq('role', 'teacher')
        .eq('is_active', true);

      if (error) {return 0;
      }

      return count || 0;
    } catch (err) {return 0;
    }
  };

  // Helper function to check if adding a teacher would exceed the limit
  const checkTeacherLimit = async (isActive = true) => {
    if (!isActive) return { allowed: true }; // Inactive teachers don't count

    const maxTeachers = await getSchoolMaxTeachers();
    if (maxTeachers === null || maxTeachers === undefined) {
      return { allowed: true }; // No limit set
    }

    const currentCount = await getActiveTeachersCount();
    if (currentCount >= maxTeachers) {
      return {
        allowed: false,
        current: currentCount,
        max: maxTeachers
      };
    }

    return { allowed: true, current: currentCount, max: maxTeachers };
  };

  // Grades are now passed as props from parent component

  // Generate random 6-digit password
  const generateRandomPassword = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Parse teachers_grade_ids array - now supports objects with grade_id and section_id
  const parseGradesToSet = (teacher) => {
    if (!teacher) return new Set();

    const selectedKeys = new Set();

    // First try to use teachers_grade_ids array
    if (teacher.teachers_grade_ids && Array.isArray(teacher.teachers_grade_ids) && teacher.teachers_grade_ids.length > 0) {
      teacher.teachers_grade_ids.forEach(item => {
        if (typeof item === 'string') {
          // Legacy format: just grade_id as string
          selectedKeys.add(item);
        } else if (typeof item === 'object' && item.grade_id) {
          // New format: object with grade_id and optional section_id
          if (item.section_id) {
            selectedKeys.add(`${item.grade_id}:${item.section_id}`);
          } else {
            selectedKeys.add(item.grade_id);
          }
        }
      });
      return selectedKeys;
    }

    // Fallback: try to parse from grade string by matching grade names to IDs
    if (teacher.grade) {
      const gradeString = teacher.grade;
      const parts = gradeString.split(',').map(g => g.trim()).filter(Boolean);
      parts.forEach(gradeName => {
        const grade = grades.find(g => g.name === gradeName);
        if (grade) {
          selectedKeys.add(grade.id);
        }
      });
    }

    return selectedKeys;
  };

  // Handle grade/section checkbox toggle
  // key can be "grade_id" for grade only, or "grade_id:section_id" for subsection
  const handleGradeToggle = (key) => {
    setSelectedGrades(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // If selecting a subsection, remove the parent grade if it exists
        if (key.includes(':')) {
          const [gradeId] = key.split(':');
          next.delete(gradeId); // Remove grade-only selection if subsection is selected
        } else {
          // If selecting a grade, remove all its subsections
          const gradeId = key;
          Array.from(next).forEach(existingKey => {
            if (existingKey.startsWith(`${gradeId}:`)) {
              next.delete(existingKey);
            }
          });
        }
        next.add(key);
      }
      return next;
    });
  };

  // Build list of grade/section options for display
  // Returns array of { key, label, gradeId, sectionId, isSection }
  // If grade has subsections, show only subsections. Otherwise, show the grade itself.
  const buildGradeSectionOptions = () => {
    const options = [];

    grades
      .filter(g => g.is_active)
      .sort((a, b) => a.order_index - b.order_index)
      .forEach(grade => {
        // Get subsections for this grade
        const sections = gradeSections
          .filter(s => s.grade_id === grade.id && s.is_active)
          .sort((a, b) => a.order_index - b.order_index);

        if (sections.length > 0) {
          // If grade has subsections, show only the subsections (not the parent grade)
          sections.forEach(section => {
            options.push({
              key: `${grade.id}:${section.id}`,
              label: `${grade.name} ${section.name}`,
              gradeId: grade.id,
              sectionId: section.id,
              isSection: true
            });
          });
        } else {
          // If grade has no subsections, show the grade itself
          options.push({
            key: grade.id,
            label: grade.name,
            gradeId: grade.id,
            sectionId: null,
            isSection: false
          });
        }
      });

    return options;
  };

  // Note: grade subsection filtering UI was removed; if reintroduced later, we can restore helper(s) here.

  // Filter and pagination logic
  useEffect(() => {
    let filtered = [...teachers];

    switch (filterType) {
      case 'alphabet':
        filtered = filtered.sort((a, b) => {
          const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
          const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case 'subject':
        if (filterValue) {
          filtered = filtered.filter(teacher => teacher.subject === filterValue);
        }
        break;
      case 'grade':
        if (filterGradeId) {
          filtered = filtered.filter(teacher => {
            // Check if teacher has this grade in teachers_grade_ids array
            if (teacher.teachers_grade_ids && Array.isArray(teacher.teachers_grade_ids)) {
              return teacher.teachers_grade_ids.some(item => {
                // Handle new object format: {grade_id, section_id}
                if (typeof item === 'object' && item.grade_id) {
                  return item.grade_id === filterGradeId;
                }
                // Handle legacy string format
                if (typeof item === 'string') {
                  return item === filterGradeId || item.startsWith(`${filterGradeId}:`);
                }
                return false;
              });
            }
            // Fallback: check grade text field
            if (teacher.grade) {
              const selectedGrade = grades.find(g => g.id === filterGradeId);
              if (selectedGrade) {
                const gradeList = teacher.grade.split(',').map(g => g.trim());
                return gradeList.some(g => g.includes(selectedGrade.name));
              }
            }
            return false;
          });
        }
        break;
      case 'status':
        if (filterValue) {
          filtered = filtered.filter(teacher => teacher.is_active === (filterValue === 'active'));
        }
        break;
      case 'role':
        if (filterRole) {
          filtered = filtered.filter(teacher => teacher.teacher_role === filterRole);
        }
        break;
      default:
        break;
    }

    setFilteredTeachers(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [teachers, filterType, filterValue, filterGradeId, filterGradeSectionId, filterRole, grades]);

  const totalPages = Math.ceil(filteredTeachers.length / TEACHERS_PER_PAGE);
  const startIndex = (currentPage - 1) * TEACHERS_PER_PAGE;
  const endIndex = startIndex + TEACHERS_PER_PAGE;
  const currentTeachers = filteredTeachers.slice(startIndex, endIndex);

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  // Check if form has been modified
  const hasFormChanges = () => {
    if (!initialFormData) return false;
    const formChanged = JSON.stringify(teacherFormData) !== JSON.stringify(initialFormData);
    const gradesChanged = initialSelectedGrades ? 
      JSON.stringify(Array.from(selectedGrades).sort()) !== JSON.stringify(Array.from(initialSelectedGrades).sort()) : 
      selectedGrades.size > 0;
    return formChanged || gradesChanged;
  };

  // Handle overlay click with unsaved changes check
  const handleOverlayClick = (closeAction) => {
    if (hasFormChanges()) {
      setPendingCloseAction(() => closeAction);
      setShowDiscardConfirm(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard changes
  const confirmDiscard = () => {
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
    setShowDiscardConfirm(false);
    setInitialFormData(null);
    setInitialSelectedGrades(null);
  };

  // Cancel discard
  const cancelDiscard = () => {
    setShowDiscardConfirm(false);
    setPendingCloseAction(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setTeacherFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'radio' ? value === 'true' : value)
    }));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    // Only allow numbers for password field
    const numericValue = value.replace(/\D/g, '').slice(0, 6); // Limit to 6 digits
    setNewPassword(numericValue);
  };

  const handlePasswordSquareClick = () => {
    const hiddenInput = document.getElementById('dashboard-school-teacher-password-input');
    if (hiddenInput) {
      hiddenInput.focus();
    }
  };

  const openTeacherDetails = (teacher) => {
    setSelectedTeacher(teacher);
    setIsEditing(false);
    setShowDetailsModal(true);
  };

  const startEditSelectedTeacher = () => {
    if (readOnly) return;
    if (!selectedTeacher) return;
    setTeacherFormData({
      first_name: selectedTeacher.first_name || '',
      last_name: selectedTeacher.last_name || '',
      email: selectedTeacher.email || '',
      phone: selectedTeacher.phone || '',
      teacher_role: selectedTeacher.teacher_role || '',
      subject: selectedTeacher.subject || '',
      language: selectedTeacher.language || '',
      is_active: selectedTeacher.is_active !== false
    });
    setNewPassword(selectedTeacher.password || ''); // Load existing password when editing

    // Parse existing grades (from teachers_grade_ids or fallback to grade string)
    const gradeSet = parseGradesToSet(selectedTeacher);
    setSelectedGrades(gradeSet);

    setIsEditing(true);
  };

  // Generate unique user_id based on school_id and incremental number
  const generateUserId = async () => {
    try {
      const schoolId = localStorage.getItem('school_id');

      // Get the school's school_id from the schools table
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('school_id')
        .eq('id', schoolId)
        .single();

      if (schoolError) {return null;
      }

      const schoolCode = schoolData.school_id;

      // Use the enhanced UserIdGenerator
      return await UserIdGenerator.generateUserId(schoolCode);
    } catch (err) {return null;
    }
  };

  const handleSubmitTeacher = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Check max teachers limit if adding as active
      if (teacherFormData.is_active) {
        const limitCheck = await checkTeacherLimit(true);
        if (!limitCheck.allowed) {
          setMaxLimitError({
            type: 'teachers',
            current: limitCheck.current,
            max: limitCheck.max
          });
          setShowMaxLimitError(true);
          setIsSubmitting(false);
          return;
        }
      }

      // Generate unique user_id
      const userId = await generateUserId();
      if (!userId) {
        alert('Error generating user ID. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Generate random 6-digit password
      const randomPassword = generateRandomPassword();
      const schoolId = localStorage.getItem('school_id');

      // Convert selected keys to object format for teachers_grade_ids
      const teachersGradeIds = Array.from(selectedGrades)
        .filter(Boolean)
        .map(key => {
          if (key.includes(':')) {
            // Subsection: format as {grade_id, section_id}
            const [gradeId, sectionId] = key.split(':');
            return { grade_id: gradeId, section_id: sectionId };
          } else {
            // Grade only: format as {grade_id, section_id: null}
            return { grade_id: key, section_id: null };
          }
        });

      // Also create grade string for backward compatibility
      const gradeNames = Array.from(selectedGrades)
        .filter(Boolean)
        .map(key => {
          const gradeId = key.includes(':') ? key.split(':')[0] : key;
          const grade = grades.find(g => g.id === gradeId);
          if (grade) {
            if (key.includes(':')) {
              const sectionId = key.split(':')[1];
              const section = gradeSections.find(s => s.id === sectionId);
              return section ? `${grade.name} ${section.name}` : grade.name;
            }
            return grade.name;
          }
          return null;
        })
        .filter(Boolean);
      const gradeString = gradeNames.length > 0 ? gradeNames.join(', ') : null;

      const { error } = await supabase
        .from('users')
        .insert([{
          user_id: userId,
          first_name: teacherFormData.first_name,
          last_name: teacherFormData.last_name,
          school_id: schoolId,
          email: teacherFormData.email,
          phone: teacherFormData.phone || null,
          role: 'teacher',
          teacher_role: teacherFormData.teacher_role && teacherFormData.teacher_role.trim() !== '' ? teacherFormData.teacher_role : null,
          subject: teacherFormData.subject || null,
          language: teacherFormData.language || null,
          grade: gradeString,
          teachers_grade_ids: teachersGradeIds.length > 0 ? teachersGradeIds : null,
          is_active: teacherFormData.is_active,
          password: randomPassword,
          created_at: new Date().toISOString()
        }])
        .select();

      if (error) {const friendlyMessage = getFriendlyErrorMessage(error, 'adding teacher');
        showNotification('error', 'Error Adding Teacher', friendlyMessage);
      } else {
        // Log the activity with school_id
        const teacherName = `${teacherFormData.first_name} ${teacherFormData.last_name}`;
        await logUserActivity.created(teacherName, 'School Teachers', schoolId);

        showNotification('success', 'Teacher Added Successfully', `${teacherName} has been added with ID: ${userId} and Password: ${randomPassword}`);
        setTeacherFormData({
          first_name: '', last_name: '', email: '', phone: '', teacher_role: '', subject: '', language: '', is_active: true, password: ''
        });
        setSelectedGrades(new Set());
        setInitialFormData(null);
        setInitialSelectedGrades(null);
        setShowAddTeacherModal(false);
        onTeacherAdded && onTeacherAdded();
      }
    } catch (err) {const friendlyMessage = getFriendlyErrorMessage(err, 'adding teacher');
      showNotification('error', 'Error Adding Teacher', friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTeacher = async (e) => {
    e.preventDefault();
    if (!selectedTeacher) return;
    setIsSubmitting(true);

    try {
      // Check max teachers limit if changing status to active
      const wasInactive = !selectedTeacher.is_active;
      const willBeActive = teacherFormData.is_active;
      if (wasInactive && willBeActive) {
        const limitCheck = await checkTeacherLimit(true);
        if (!limitCheck.allowed) {
          setMaxLimitError({
            type: 'teachers',
            current: limitCheck.current,
            max: limitCheck.max
          });
          setShowMaxLimitError(true);
          setIsSubmitting(false);
          return;
        }
      }

      // Convert selected keys to object format for teachers_grade_ids
      const teachersGradeIds = Array.from(selectedGrades)
        .filter(Boolean)
        .map(key => {
          if (key.includes(':')) {
            // Subsection: format as {grade_id, section_id}
            const [gradeId, sectionId] = key.split(':');
            return { grade_id: gradeId, section_id: sectionId };
          } else {
            // Grade only: format as {grade_id, section_id: null}
            return { grade_id: key, section_id: null };
          }
        });

      // Also create grade string for backward compatibility
      const gradeNames = Array.from(selectedGrades)
        .filter(Boolean)
        .map(key => {
          const gradeId = key.includes(':') ? key.split(':')[0] : key;
          const grade = grades.find(g => g.id === gradeId);
          if (grade) {
            if (key.includes(':')) {
              const sectionId = key.split(':')[1];
              const section = gradeSections.find(s => s.id === sectionId);
              return section ? `${grade.name} ${section.name}` : grade.name;
            }
            return grade.name;
          }
          return null;
        })
        .filter(Boolean);
      const gradeString = gradeNames.length > 0 ? gradeNames.join(', ') : null;

      // Build update object
      const updateData = {
        first_name: teacherFormData.first_name,
        last_name: teacherFormData.last_name,
        email: teacherFormData.email,
        phone: teacherFormData.phone || null,
        teacher_role: teacherFormData.teacher_role && teacherFormData.teacher_role.trim() !== '' ? teacherFormData.teacher_role : null,
        subject: teacherFormData.subject || null,
        language: teacherFormData.language || null,
        grade: gradeString,
        teachers_grade_ids: teachersGradeIds.length > 0 ? teachersGradeIds : null,
        is_active: teacherFormData.is_active,
        updated_at: new Date().toISOString()
      };

      // Only update password if a new one is provided
      if (newPassword && newPassword.trim() !== '') {
        updateData.password = newPassword.trim();
      }

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', selectedTeacher.id);

      if (error) {const friendlyMessage = getFriendlyErrorMessage(error, 'updating teacher');
        showNotification('error', 'Error Updating Teacher', friendlyMessage);
      } else {
        // Log the activity with school_id
        const schoolId = localStorage.getItem('school_id');
        const teacherName = `${teacherFormData.first_name} ${teacherFormData.last_name}`;
        await logUserActivity.updated(teacherName, schoolId);

        const passwordMsg = newPassword && newPassword.trim() !== '' ? ' Password has been updated.' : '';
        showNotification('success', 'Teacher Updated Successfully', `${teacherName} has been updated successfully.${passwordMsg}`);
        setShowDetailsModal(false);
        setIsEditing(false);
        setNewPassword(''); // Clear password field after successful update
        onTeacherAdded && onTeacherAdded();
      }
    } catch (err) {const friendlyMessage = getFriendlyErrorMessage(err, 'updating teacher');
      showNotification('error', 'Error Updating Teacher', friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTeacher = () => {
    if (readOnly) return;
    if (!selectedTeacher) return;
    setItemToDelete({ type: 'teacher', data: selectedTeacher });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (readOnly) return;
    if (!itemToDelete) return;

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', itemToDelete.data.id);

      if (error) {showNotification('error', 'Error Deleting Teacher', error.message);
      } else {
        // Log the activity with school_id
        const schoolId = localStorage.getItem('school_id');
        const teacherName = `${itemToDelete.data.first_name} ${itemToDelete.data.last_name}`;
        await logUserActivity.deleted(teacherName, schoolId);

        showNotification('success', 'Teacher Deleted Successfully', `${teacherName} has been deleted successfully`);
        setShowDetailsModal(false);
        setSelectedTeacher(null);
        setShowDeleteConfirm(false);
        setItemToDelete(null);
        onTeacherAdded && onTeacherAdded();
      }
    } catch (err) {showNotification('error', 'Error Deleting Teacher', err.message);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  // Bulk selection functions
  const toggleSelectionMode = () => {
    if (readOnly) return;
    setSelectionMode(!selectionMode);
    setSelectedTeachers(new Set());
  };

  const toggleSelectAll = () => {
    if (selectedTeachers.size === currentTeachers.length) {
      setSelectedTeachers(new Set());
    } else {
      setSelectedTeachers(new Set(currentTeachers.map(t => t.id)));
    }
  };

  const toggleSelectTeacher = (teacherId) => {
    const newSelected = new Set(selectedTeachers);
    if (newSelected.has(teacherId)) {
      newSelected.delete(teacherId);
    } else {
      newSelected.add(teacherId);
    }
    setSelectedTeachers(newSelected);
  };

  const handleBulkDelete = () => {
    if (readOnly) return;
    if (selectedTeachers.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    if (readOnly) return;
    try {
      const teacherIds = Array.from(selectedTeachers);
      const { error } = await supabase
        .from('users')
        .delete()
        .in('id', teacherIds);

      if (error) {showNotification('error', 'Bulk Delete Failed', error.message);
      } else {
        const schoolId = localStorage.getItem('school_id');
        await logUserActivity.deleted(`${teacherIds.length} teachers (bulk delete)`, schoolId);

        showNotification('success', 'Teachers Deleted Successfully', `${teacherIds.length} teacher(s) have been deleted`);
        setSelectedTeachers(new Set());
        setSelectionMode(false);
        setShowBulkDeleteConfirm(false);
        onTeacherAdded && onTeacherAdded();
      }
    } catch (err) {showNotification('error', 'Bulk Delete Failed', err.message);
    }
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteConfirm(false);
  };

  const closeModal = () => {
    setShowAddTeacherModal(false);
    setAddMode('single');
    setExcelFile(null);
    setExcelSelectedGrades(new Set());
    setTeacherFormData({
      first_name: '', last_name: '', email: '', phone: '', teacher_role: '', subject: '', language: '', is_active: true, password: ''
    });
    setSelectedGrades(new Set());
    setShowUploadResults(false);
    setUploadResults(null);
    setShowDeleteByExcelModal(false);
    setDeleteExcelFile(null);
    setDeleteExcelResults(null);
    setInitialFormData(null);
    setInitialSelectedGrades(null);
  };

  const downloadDeleteExcelTemplate = () => {
    const templateData = [
      { first_name: 'John', last_name: 'Doe', email: 'john.doe@example.com', phone: '+1234567890' },
      { first_name: 'Jane', last_name: 'Smith', email: 'jane.smith@example.com', phone: '+1234567891' }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Teachers');

    XLSX.writeFile(wb, 'delete_teachers_template.xls');
  };

  const downloadFailedDeleteReport = () => {
    if (!deleteExcelResults || !deleteExcelResults.failed || deleteExcelResults.failed.length === 0) return;

    // Helper function to create safe sheet names
    const createSafeSheetName = (name) => {
      let safeName = name
        .replace(/:/g, ' -')
        .replace(/[\\/?*[\]]/g, '')
        .substring(0, 31);
      return safeName || 'Unknown';
    };

    // Group failed teachers by reason
    const groupedByReason = deleteExcelResults.failed.reduce((acc, item) => {
      const reason = item.reason || 'Unknown error';
      if (!acc[reason]) {
        acc[reason] = [];
      }
      acc[reason].push(item);
      return acc;
    }, {});

    const wb = XLSX.utils.book_new();

    // Create summary sheet
    const summaryData = [
      { 'Summary': 'Failed to Deactivate Teachers Report', '': '' },
      { 'Summary': '', '': '' },
      { 'Summary': 'Total Failed', 'Count': deleteExcelResults.failed.length },
      { 'Summary': 'Total Processed', 'Count': deleteExcelResults.total },
      { 'Summary': 'Successfully Deactivated', 'Count': deleteExcelResults.success },
      { 'Summary': '', '': '' },
      { 'Summary': 'Failed by Reason', 'Count': '' },
    ];

    Object.entries(groupedByReason).forEach(([reason, teachers]) => {
      summaryData.push({
        'Summary': reason,
        'Count': teachers.length
      });
    });

    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    // Create separate sheet for each reason
    Object.entries(groupedByReason).forEach(([reason, teachers]) => {
      const sheetData = teachers.map(item => ({
        'Row': item.row || '',
        'First Name': item.first_name || '',
        'Last Name': item.last_name || '',
        'Email': item.email || '',
        'Phone': item.phone || '',
        'Subject': item.subject || ''
      }));

      const ws = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = createSafeSheetName(reason);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, 'failed_delete_teachers_report.xlsx');
  };

  const handleDeleteExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processDeleteExcelFile(file);
    }
  };

  const processDeleteExcelFile = (file) => {
    const name = (file?.name || '').toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    if (file && isExcel) {
      setDeleteExcelFile(file);
    } else {
      showNotification('error', 'Invalid File', 'Please select a valid Excel file (.xlsx or .xls)');
    }
  };

  const handleDeleteExcelDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setDeleteExcelDragging(true);
  };

  const handleDeleteExcelDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDeleteExcelDragging(false);
    }
  };

  const handleDeleteExcelDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteExcelDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processDeleteExcelFile(files[0]);
    }
  };

  const handleDeleteByExcel = async () => {
    if (!deleteExcelFile) {
      showNotification('error', 'Delete Failed', 'Please select an Excel file.');
      return;
    }

    setIsDeleteExcelUploading(true);

    try {
      const rows = await readExcelFirstSheet(deleteExcelFile);
      if (!rows || rows.length === 0) {
        showNotification('error', 'Delete Failed', 'Your Excel file is empty (no rows found).');
        return;
      }

      const schoolId = localStorage.getItem('school_id');

      const toDeactivate = [];
      const failed = [];
      const seenEmails = new Set();

      for (let i = 0; i < rows.length; i++) {
        const originalRow = rows[i];
        const normalizedRow = {};
        Object.entries(originalRow || {}).forEach(([k, v]) => {
          const nk = normalizeExcelHeader(k);
          if (nk) normalizedRow[nk] = typeof v === 'string' ? v.trim() : v;
        });

        const email = String(
          getExcelValue(normalizedRow, ['email', 'email_address', 'mail'])
        ).trim();
        const phone = String(
          getExcelValue(normalizedRow, ['phone', 'phone_number', 'mobile'])
        ).trim();
        const subject = String(getExcelValue(normalizedRow, ['subject', 'subjects'])).trim();

        // Skip blank rows
        if (!email) continue;

        // Validate email format
        if (!validateEmail(email)) {
          failed.push({
            row: i + 2,
            first_name: String(getExcelValue(normalizedRow, ['first_name', 'firstname', 'first'])).trim() || '',
            last_name: String(getExcelValue(normalizedRow, ['last_name', 'lastname', 'last', 'surname'])).trim() || '',
            email: email,
            phone: phone,
            subject: subject,
            reason: 'Invalid email format'
          });
          continue;
        }

        // Check for duplicate email within the Excel file
        if (seenEmails.has(email.toLowerCase())) {
          failed.push({
            row: i + 2,
            first_name: String(getExcelValue(normalizedRow, ['first_name', 'firstname', 'first'])).trim() || '',
            last_name: String(getExcelValue(normalizedRow, ['last_name', 'lastname', 'last', 'surname'])).trim() || '',
            email: email,
            phone: phone,
            subject: subject,
            reason: 'Email already exists in this Excel file'
          });
          continue;
        }
        seenEmails.add(email.toLowerCase());

        toDeactivate.push(email.toLowerCase());
      }

      if (toDeactivate.length === 0) {
        showNotification('error', 'Delete Failed', 'No valid emails found in the Excel file.');
        return;
      }

      // Find teachers by email in the database
      const CHECK_CHUNK = 200;
      const foundTeachers = [];

      for (let i = 0; i < toDeactivate.length; i += CHECK_CHUNK) {
        const chunk = toDeactivate.slice(i, i + CHECK_CHUNK);
        const { data, error } = await supabase
          .from('users')
          .select('id, email, first_name, last_name, is_active, phone, subject')
          .eq('school_id', schoolId)
          .eq('role', 'teacher')
          .in('email', chunk);

        if (error) {continue;
        }

        if (data) {
          foundTeachers.push(...data);
        }
      }

      // Create a map of found teachers by email
      const foundTeachersMap = new Map();
      foundTeachers.forEach(teacher => {
        foundTeachersMap.set(teacher.email.toLowerCase(), teacher);
      });

      // Separate found and not found
      const foundEmails = [];
      const notFoundEmails = [];

      toDeactivate.forEach(email => {
        const teacher = foundTeachersMap.get(email);
        if (teacher) {
          foundEmails.push({ email, teacher });
        } else {
          // Find the row data for this email
          for (let i = 0; i < rows.length; i++) {
            const originalRow = rows[i];
            const normalizedRow = {};
            Object.entries(originalRow || {}).forEach(([k, v]) => {
              const nk = normalizeExcelHeader(k);
              if (nk) normalizedRow[nk] = typeof v === 'string' ? v.trim() : v;
            });
            const rowEmail = String(getExcelValue(normalizedRow, ['email', 'email_address', 'mail'])).trim().toLowerCase();
            if (rowEmail === email) {
              notFoundEmails.push({
                row: i + 2,
                first_name: String(getExcelValue(normalizedRow, ['first_name', 'firstname', 'first'])).trim() || '',
                last_name: String(getExcelValue(normalizedRow, ['last_name', 'lastname', 'last', 'surname'])).trim() || '',
                email: email,
                phone: String(getExcelValue(normalizedRow, ['phone', 'phone_number', 'mobile'])).trim() || '',
                subject: String(getExcelValue(normalizedRow, ['subject', 'subjects'])).trim() || ''
              });
              break;
            }
          }
        }
      });

      // Mark found teachers as inactive
      const CHUNK_SIZE = 50;
      let deactivatedCount = 0;
      const deactivationErrors = [];

      for (let i = 0; i < foundEmails.length; i += CHUNK_SIZE) {
        const chunk = foundEmails.slice(i, i + CHUNK_SIZE);
        const teacherIds = chunk.map(item => item.teacher.id);

        const { error } = await supabase
          .from('users')
          .update({
            is_active: false,
            updated_at: new Date().toISOString()
          })
          .in('id', teacherIds);

        if (error) {chunk.forEach(item => {
            failed.push({
              row: null,
              first_name: item.teacher.first_name || '',
              last_name: item.teacher.last_name || '',
              email: item.email,
              phone: item.teacher.phone || '',
              subject: item.teacher.subject || '',
              reason: error.message || 'Failed to deactivate teacher'
            });
          });
          deactivationErrors.push(error.message);
        } else {
          deactivatedCount += chunk.length;
        }
      }

      // Add not found emails to failed list
      notFoundEmails.forEach(item => {
        failed.push({
          ...item,
          reason: 'Teacher not found in the system'
        });
      });

      // Log activity
      if (deactivatedCount > 0) {
        await logUserActivity.updated(`${deactivatedCount} teachers deactivated (Excel)`, schoolId);
      }

      // Set results
      const results = {
        total: rows.length,
        success: deactivatedCount,
        failed: failed,
        hasErrors: failed.length > 0 || deactivationErrors.length > 0
      };

      setDeleteExcelResults(results);
      setShowDeleteExcelResults(true);

      // Refresh teacher list if any were deactivated
      if (deactivatedCount > 0) {
        onTeacherAdded && onTeacherAdded();
      }
    } catch (err) {showNotification('error', 'Delete Failed', err.message || 'Unable to process the Excel file.');
    } finally {
      setIsDeleteExcelUploading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') return { valid: false, reason: 'Phone number is required' };
    // Basic phone validation - allow digits, spaces, dashes, parentheses, plus sign
    const phoneRegex = /^[\d\s\-+()]+$/;
    if (!phoneRegex.test(phone)) {
      return { valid: false, reason: 'Invalid phone number format' };
    }
    // Check minimum length (at least 7 digits)
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 7) {
      return { valid: false, reason: 'Phone number is too short' };
    }
    return { valid: true };
  };

  const downloadExcelTemplate = () => {
    const templateData = [
      { first_name: 'John', last_name: 'Doe', email: 'john.doe@example.com', phone: '+1234567890', subject: 'Mathematics' },
      { first_name: 'Jane', last_name: 'Smith', email: 'jane.smith@example.com', phone: '+1234567891', subject: 'English' }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Teachers');

    XLSX.writeFile(wb, 'teachers_template.xls');
  };

  const downloadFailedUsersReport = () => {
    if (!uploadResults || !uploadResults.failed || uploadResults.failed.length === 0) return;

    // Helper function to create safe sheet names (Excel has restrictions)
    const createSafeSheetName = (name) => {
      // Excel sheet names can't be longer than 31 characters and can't contain certain characters
      let safeName = name
        .replace(/:/g, ' -') // Replace colons first
        .replace(/[\\/?*[\]]/g, '') // Remove invalid characters
        .substring(0, 31); // Limit to 31 characters
      return safeName || 'Unknown';
    };

    // Group failed teachers by reason
    const groupedByReason = uploadResults.failed.reduce((acc, item) => {
      const reason = item.reason || 'Unknown error';
      if (!acc[reason]) {
        acc[reason] = [];
      }
      acc[reason].push(item);
      return acc;
    }, {});

    const wb = XLSX.utils.book_new();

    // Create a summary sheet with overview
    const summaryData = [
      { 'Summary': 'Failed Teachers Upload Report', '': '' },
      { 'Summary': '', '': '' },
      { 'Summary': 'Total Failed', 'Count': uploadResults.failed.length },
      { 'Summary': 'Total Processed', 'Count': uploadResults.total },
      { 'Summary': 'Successfully Added', 'Count': uploadResults.success },
      { 'Summary': '', '': '' },
      { 'Summary': 'Failed by Reason', 'Count': '' },
    ];

    Object.entries(groupedByReason).forEach(([reason, teachers]) => {
      summaryData.push({
        'Summary': reason,
        'Count': teachers.length
      });
    });

    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    // Create a separate sheet for each reason
    Object.entries(groupedByReason).forEach(([reason, teachers]) => {
      const sheetData = teachers.map(item => ({
        'Row': item.row || '',
        'First Name': item.first_name || '',
        'Last Name': item.last_name || '',
        'Email': item.email || '',
        'Phone': item.phone || '',
        'Subject': item.subject || ''
      }));

      const ws = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = createSafeSheetName(reason);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, 'failed_teachers_report.xlsx');
  };

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processExcelFile(file);
    }
  };

  const processExcelFile = (file) => {
    const name = (file?.name || '').toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    if (file && isExcel) {
      setExcelFile(file);
    } else {
      showNotification('error', 'Invalid File', 'Please select a valid Excel file (.xlsx or .xls)');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (canUploadFile()) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDragging(true);
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!canUploadFile()) {
      showNotification('warning', 'Cannot Upload', 'Please select at least one grade first.');
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processExcelFile(files[0]);
    }
  };

  const canUploadFile = () => {
    return excelSelectedGrades.size > 0;
  };

  // Excel handling functions - handleExcelFileChange is now defined below with processExcelFile

  const normalizeExcelHeader = (header) => {
    if (header === null || header === undefined) return '';
    return String(header)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  };

  const getExcelValue = (row, keys) => {
    for (const k of keys) {
      if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') {
        return row[k];
      }
    }
    return '';
  };

  const readExcelFirstSheet = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames?.[0];
          if (!firstSheetName) return resolve([]);
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          resolve(rows || []);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read the Excel file'));
      reader.readAsArrayBuffer(file);
    });

  const normalizeGrade = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const lower = raw.toLowerCase();
    if (lower.startsWith('kg')) {
      const num = raw.replace(/kg/i, '').trim().replace(/\s+/g, '');
      return num ? `KG${num}` : raw;
    }
    if (lower.startsWith('grade')) {
      const num = raw.replace(/grade/i, '').trim();
      return num ? `Grade ${num}` : raw;
    }
    if (/^\d+$/.test(raw)) return `Grade ${raw}`;
    return raw;
  };

  const normalizeGradeList = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const parts = raw
      .split(/,|and/i)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length <= 1) return normalizeGrade(raw);
    const normalizedParts = parts.map((p) => normalizeGrade(p));
    return normalizedParts.join(', ');
  };

  const parseIsActive = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return true;
    if (['active', 'true', 'yes', 'y', '1'].includes(raw)) return true;
    if (['inactive', 'false', 'no', 'n', '0'].includes(raw)) return false;
    return true;
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      showNotification('error', 'Excel Import Failed', 'Please select an Excel file.');
      return;
    }

    if (excelSelectedGrades.size === 0) {
      showNotification('error', 'Excel Import Failed', 'Please select at least one grade.');
      return;
    }

    setIsExcelUploading(true);

    try {
      const rows = await readExcelFirstSheet(excelFile);
      if (!rows || rows.length === 0) {
        showNotification('error', 'Excel Import Failed', 'Your Excel file is empty (no rows found).');
        return;
      }

      const schoolId = localStorage.getItem('school_id');
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('school_id, name')
        .eq('id', schoolId)
        .single();

      if (schoolError || !schoolData) {
        showNotification('error', 'Excel Import Failed', 'Could not fetch school information.');
        return;
      }

      const schoolCode = schoolData.school_id.toLowerCase();
      // const schoolName = schoolData.name;
      let nextNumber = await UserIdGenerator.getNextUserNumber(schoolCode);
      if (!nextNumber || Number.isNaN(nextNumber)) nextNumber = 1;

      // Build teachers_grade_ids from selected grades
      const teachersGradeIds = Array.from(excelSelectedGrades)
        .filter(Boolean)
        .map(key => {
          if (key.includes(':')) {
            const [gradeId, sectionId] = key.split(':');
            return { grade_id: gradeId, section_id: sectionId };
          } else {
            return { grade_id: key, section_id: null };
          }
        });

      // Build grade string for backward compatibility
      const gradeNames = Array.from(excelSelectedGrades)
        .filter(Boolean)
        .map(key => {
          const gradeId = key.includes(':') ? key.split(':')[0] : key;
          const grade = grades.find(g => g.id === gradeId);
          if (grade) {
            if (key.includes(':')) {
              const sectionId = key.split(':')[1];
              const section = gradeSections.find(s => s.id === sectionId);
              return section ? `${grade.name} ${section.name}` : grade.name;
            }
            return grade.name;
          }
          return null;
        })
        .filter(Boolean);
      const gradeString = gradeNames.length > 0 ? gradeNames.join(', ') : null;

      const toInsert = [];
      const failed = [];
      const seenUserIds = new Set();
      const seenEmails = new Set();

      for (let i = 0; i < rows.length; i++) {
        const originalRow = rows[i];
        const normalizedRow = {};
        Object.entries(originalRow || {}).forEach(([k, v]) => {
          const nk = normalizeExcelHeader(k);
          if (nk) normalizedRow[nk] = typeof v === 'string' ? v.trim() : v;
        });

        const firstName = String(
          getExcelValue(normalizedRow, ['first_name', 'firstname', 'first'])
        ).trim();
        const lastName = String(
          getExcelValue(normalizedRow, ['last_name', 'lastname', 'last', 'surname'])
        ).trim();
        const email = String(
          getExcelValue(normalizedRow, ['email', 'email_address', 'mail'])
        ).trim();
        const phone = String(
          getExcelValue(normalizedRow, ['phone', 'phone_number', 'mobile'])
        ).trim();
        const subject = String(getExcelValue(normalizedRow, ['subject', 'subjects'])).trim();

        // Skip blank rows  
        if (!firstName && !lastName && !email) continue;

        // Validate required fields
        if (!firstName) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            grade: gradeString,
            subject: subject,
            reason: 'First name is required'
          });
          continue;
        }

        if (!email) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            grade: gradeString,
            subject: subject,
            reason: 'Email address is required'
          });
          continue;
        }

        // Validate email format
        if (!validateEmail(email)) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            grade: gradeString,
            subject: subject,
            reason: 'Invalid email format'
          });
          continue;
        }

        // Validate phone
        const phoneValidation = validatePhone(phone);
        if (!phoneValidation.valid) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            grade: gradeString,
            subject: subject,
            reason: phoneValidation.reason
          });
          continue;
        }

        // Validate subject
        if (!subject) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            grade: gradeString,
            subject: subject,
            reason: 'Subject is required'
          });
          continue;
        }

        // Check for duplicate email within the Excel file
        if (seenEmails.has(email.toLowerCase())) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            grade: gradeString,
            subject: subject,
            reason: 'Email already exists in this Excel file'
          });
          continue;
        }
        seenEmails.add(email.toLowerCase());

        // Generate userId
        const userId = `${schoolCode}${nextNumber++}`;
        if (seenUserIds.has(userId)) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            grade: gradeString,
            subject: subject,
            reason: 'Student ID already exists in this Excel file'
          });
          continue;
        }
        seenUserIds.add(userId);

        // Always generate random 6-digit password
        const finalPassword = generateRandomPassword();

        toInsert.push({
          user_id: userId,
          first_name: firstName,
          last_name: lastName,
          school_id: schoolId,
          email,
          phone: phone || null,
          role: 'teacher',
          grade: gradeString,
          teachers_grade_ids: teachersGradeIds.length > 0 ? teachersGradeIds : null,
          subject: subject,
          is_active: true, // Always active by default
          password: finalPassword,
          created_at: new Date().toISOString()
        });
      }

      // Check for existing emails in database
      const emailsToCheck = toInsert.map(u => u.email);
      const existingEmails = new Set();
      const CHECK_CHUNK = 200;

      if (emailsToCheck.length > 0) {
        for (let i = 0; i < emailsToCheck.length; i += CHECK_CHUNK) {
          const chunk = emailsToCheck.slice(i, i + CHECK_CHUNK);
          const { data } = await supabase
            .from('users')
            .select('email')
            .eq('school_id', schoolId)
            .in('email', chunk);
          (data || []).forEach((u) => existingEmails.add(u.email.toLowerCase()));
        }
      }

      // Check max teachers limit before inserting
      const maxTeachers = await getSchoolMaxTeachers();
      if (maxTeachers !== null && maxTeachers !== undefined) {
        const currentCount = await getActiveTeachersCount();
        const activeToInsert = toInsert.filter(u => u.is_active === true).length;

        if (currentCount + activeToInsert > maxTeachers) {
          const canInsert = maxTeachers - currentCount;
          if (canInsert <= 0) {
            showNotification('error', 'Excel Import Failed', `Maximum number of active teachers (${maxTeachers}) has been reached. Cannot add any more teachers.`);
            setIsExcelUploading(false);
            return;
          }

          // Mark excess active teachers as failed and filter them out
          let activeCount = 0;
          const filteredForLimit = [];
          toInsert.forEach((user) => {
            if (user.is_active && activeCount >= canInsert) {
              failed.push({
                row: null,
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                phone: user.phone,
                grade: gradeString,
                subject: user.subject,
                reason: `Maximum number of active teachers (${maxTeachers}) reached. Only ${canInsert} more can be added.`
              });
            } else {
              if (user.is_active) activeCount++;
              filteredForLimit.push(user);
            }
          });

          // Replace toInsert with filtered array
          toInsert.length = 0;
          toInsert.push(...filteredForLimit);
        }
      }

      // Remove duplicates from toInsert and add to failed list
      const filtered = [];
      toInsert.forEach((user) => {
        if (existingEmails.has(user.email.toLowerCase())) {
          failed.push({
            row: null,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            phone: user.phone,
            grade: gradeString,
            subject: user.subject,
            reason: 'Email address already exists in the system'
          });
        } else {
          filtered.push(user);
        }
      });

      // Insert in chunks
      const CHUNK_SIZE = 50;
      let insertedCount = 0;
      const insertErrors = [];

      for (let i = 0; i < filtered.length; i += CHUNK_SIZE) {
        const chunk = filtered.slice(i, i + CHUNK_SIZE);
        const { error } = await supabase.from('users').insert(chunk);

        if (error) {chunk.forEach((user) => {
            failed.push({
              row: null,
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email,
              phone: user.phone,
              grade: gradeString,
              subject: user.subject,
              reason: error.message || 'Failed to add teacher to database'
            });
          });
          insertErrors.push(error.message);
        } else {
          insertedCount += chunk.length;
        }
      }

      // Log activity
      if (insertedCount > 0) {
        const summaryName = `${insertedCount} teachers imported (Excel)`;
        await logUserActivity.created(summaryName, 'School Teachers', schoolId);
      }

      // Set upload results
      const results = {
        total: rows.length,
        success: insertedCount,
        failed: failed,
        hasErrors: failed.length > 0 || insertErrors.length > 0
      };

      setUploadResults(results);
      setShowUploadResults(true);

      // Refresh teacher list if any were added
      if (insertedCount > 0) {
        onTeacherAdded && onTeacherAdded();
      }
    } catch (err) {showNotification('error', 'Excel Import Failed', err.message || 'Unable to import the Excel file.');
    } finally {
      setIsExcelUploading(false);
    }
  };

  return (
    <div className="school-teacher-content">
      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="school-teacher-content-header">
        <h2>Teacher Management ({activeTeachersCount}{maxTeachers !== null && maxTeachers !== undefined ? ` of ${maxTeachers}` : ''})</h2>
        <div className="school-teacher-header-actions">
          {!selectionMode && (
            <>
              <button className="school-teachers-filter-button" onClick={() => setShowFilterModal(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                </svg>
                Filter
              </button>
              {!readOnly && (
                <>
                  <button className="school-teachers-select-btn" onClick={toggleSelectionMode}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 11l3 3L22 4"></path>
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                    </svg>
                    Select
                  </button>
                  <button className="school-teacher-add-button" onClick={() => setShowAddTeacherModal(true)}>+ Add Teacher</button>
                </>
              )}
            </>
          )}

          {!readOnly && selectionMode && (
            <>
              <button className="school-teachers-cancel-selection-btn" onClick={toggleSelectionMode}>
                Cancel
              </button>
              <button
                className="school-teachers-delete-excel-btn"
                onClick={() => setShowDeleteByExcelModal(true)}
                style={{
                  background: '#dc3545',
                  border: '1px solid #dc3545',
                  color: '#fff',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                Delete by Excel
              </button>
              {selectedTeachers.size > 0 && (
                <button className="school-teachers-delete-selected-btn" onClick={handleBulkDelete}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                  Delete Selected ({selectedTeachers.size})
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {dataLoading ? (
        <div className="school-teacher-loading-state">
          <div className="school-teacher-loading-spinner">⏳</div>
          <p>Loading teachers...</p>
        </div>
      ) : teachers.length === 0 ? (
        <NoDataState
          type="Teachers"
          icon="👨‍🏫"
          message="No teachers have been added yet. Add your first teacher to get started."
          actionText="+ Add First Teacher"
          onAction={() => setShowAddTeacherModal(true)}
        />
      ) : (
        <>
          <div className="school-teacher-table">
            <div className={`school-teacher-table-header ${selectionMode ? 'with-checkbox' : ''}`}>
              {selectionMode && (
                <div className="school-teacher-table-cell school-teacher-header-cell school-teacher-checkbox-cell">
                  <input
                    type="checkbox"
                    checked={selectedTeachers.size === currentTeachers.length && currentTeachers.length > 0}
                    onChange={toggleSelectAll}
                    className="school-teacher-select-checkbox"
                  />
                </div>
              )}
              <div className="school-teacher-table-cell school-teacher-header-cell">Name</div>
              <div className="school-teacher-table-cell school-teacher-header-cell">Email</div>
              <div className="school-teacher-table-cell school-teacher-header-cell">Subject</div>
              <div className="school-teacher-table-cell school-teacher-header-cell">Status</div>
            </div>

            {currentTeachers.map((teacher) => (
              <div
                key={teacher.id}
                className={`school-teacher-table-row ${selectionMode ? 'with-checkbox' : ''} ${selectedTeachers.has(teacher.id) ? 'selected' : ''}`}
                onClick={(e) => {
                  if (selectionMode) {
                    e.stopPropagation();
                    toggleSelectTeacher(teacher.id);
                  } else {
                    openTeacherDetails(teacher);
                  }
                }}
              >
                {selectionMode && (
                  <div className="school-teacher-table-cell school-teacher-data-cell school-teacher-checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedTeachers.has(teacher.id)}
                      onChange={() => toggleSelectTeacher(teacher.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="school-teacher-select-checkbox"
                    />
                  </div>
                )}
                <div className="school-teacher-table-cell school-teacher-data-cell">
                  <div className="school-teacher-info">
                    <div className="school-teacher-avatar">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z" />
                      </svg>
                    </div>
                    <div className="school-teacher-name-info">
                      <div className="school-teacher-name-main">{teacher.full_name || `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim() || 'Unnamed Teacher'}</div>
                      <div className="school-teacher-email-small">{teacher.email || 'No email'}</div>
                    </div>
                  </div>
                </div>
                <div className="school-teacher-table-cell school-teacher-data-cell">
                  <div className="school-teacher-email-info">
                    <span className="school-teacher-email-text">{teacher.email || 'No email'}</span>
                  </div>
                </div>
                <div className="school-teacher-table-cell school-teacher-data-cell">
                  <div className="school-teacher-subject-info">
                    <span className="school-teacher-subject-badge">{teacher.subject || 'No subject'}</span>
                  </div>
                </div>
                <div className="school-teacher-table-cell school-teacher-data-cell school-teacher-status-cell">
                  <div className="school-teacher-status-info">
                    <span className={`school-teacher-status-badge ${teacher.is_active ? 'school-teacher-status-active' : 'school-teacher-status-inactive'}`}>
                      {teacher.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="school-teacher-pagination">
              <button
                className="school-teacher-pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="school-teacher-pagination-info">
                Page {currentPage} of {totalPages} ({filteredTeachers.length} teachers)
              </span>
              <button
                className="school-teacher-pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Teacher Details / Edit Modal */}
      {showDetailsModal && selectedTeacher && (
        <div className="school-teacher-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="school-teacher-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="school-teacher-modal-header">
              <h2>{isEditing ? 'Edit Teacher' : 'Teacher Details'}</h2>
              <button className="school-teacher-modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            {!isEditing ? (
              <div className="school-teacher-details">
                <div className="school-teacher-details-section">
                  <div className="school-teacher-section-header">
                    <div className="school-teacher-section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z" />
                      </svg>
                    </div>
                    <h4>Personal Information</h4>
                  </div>
                  <div className="school-teacher-details-grid">
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">First Name</span>
                      <span className="school-teacher-detail-value">{selectedTeacher.first_name || '—'}</span>
                    </div>
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Last Name</span>
                      <span className="school-teacher-detail-value">{selectedTeacher.last_name || '—'}</span>
                    </div>
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Full Name</span>
                      <span className="school-teacher-detail-value">{selectedTeacher.full_name || '—'}</span>
                    </div>
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Teacher ID</span>
                      <span className="school-teacher-detail-value">{selectedTeacher.user_id || '—'}</span>
                    </div>
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Email</span>
                      <span className="school-teacher-detail-value">{selectedTeacher.email || '—'}</span>
                    </div>
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Phone</span>
                      <span className="school-teacher-detail-value">{selectedTeacher.phone || '—'}</span>
                    </div>
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Teacher Role</span>
                      <span className="school-teacher-detail-value">
                        {selectedTeacher.teacher_role
                          ? teacherRoles.find(r => r.value === selectedTeacher.teacher_role)?.label || selectedTeacher.teacher_role
                          : '—'}
                      </span>
                    </div>
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Subject</span>
                      <span className="school-teacher-detail-value">{selectedTeacher.subject || '—'}</span>
                    </div>

                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Grades</span>
                      <span className="school-teacher-detail-value">{selectedTeacher.grade || '—'}</span>
                    </div>
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Password</span>
                      <span className="school-teacher-detail-value school-teacher-password-value">{selectedTeacher.password || '—'}</span>
                    </div>
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Status</span>
                      <span className={`school-teacher-status-badge ${selectedTeacher.is_active ? 'school-teacher-status-active' : 'school-teacher-status-inactive'}`}>
                        {selectedTeacher.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="school-teacher-section-divider"></div>

                <div className="school-teacher-details-section">
                  <div className="school-teacher-section-header">
                    <div className="school-teacher-section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z" />
                      </svg>
                    </div>
                    <h4>System Information</h4>
                  </div>
                  <div className="school-teacher-details-grid">
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Created</span>
                      <span className="school-teacher-detail-value">{selectedTeacher.created_at ? new Date(selectedTeacher.created_at).toLocaleDateString() : '—'}</span>
                    </div>
                    <div className="school-teacher-detail-item">
                      <span className="school-teacher-detail-label">Last Updated</span>
                      <span className="school-teacher-detail-value">{selectedTeacher.updated_at ? new Date(selectedTeacher.updated_at).toLocaleDateString() : '—'}</span>
                    </div>
                  </div>
                </div>

                <div className="school-teacher-form-actions">
                  {!readOnly && (
                    <>
                      <button type="button" className="school-teacher-cancel-btn" onClick={handleDeleteTeacher}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                        </svg>
                        Delete Teacher
                      </button>
                      <button type="button" className="school-teacher-submit-btn" onClick={startEditSelectedTeacher}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.71 7.04C21.1 6.65 21.1 6 20.71 5.63L18.37 3.29C18 2.9 17.35 2.9 16.96 3.29L15.12 5.12L18.87 8.87M3 17.25V21H6.75L17.81 9.93L14.06 6.18L3 17.25Z" />
                        </svg>
                        Edit Teacher
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpdateTeacher} className="school-teacher-form">
                <div className="school-teacher-form-row">
                  <div className="school-teacher-form-group">
                    <label htmlFor="first_name">First Name *</label>
                    <input type="text" id="first_name" name="first_name" value={teacherFormData.first_name} onChange={handleInputChange} required />
                  </div>
                  <div className="school-teacher-form-group">
                    <label htmlFor="last_name">Last Name *</label>
                    <input type="text" id="last_name" name="last_name" value={teacherFormData.last_name} onChange={handleInputChange} required />
                  </div>
                </div>

                <div className="school-teacher-form-row">
                  <div className="school-teacher-form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input type="email" id="email" name="email" value={teacherFormData.email} onChange={handleInputChange} required />
                  </div>
                  <div className="school-teacher-form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input type="tel" id="phone" name="phone" value={teacherFormData.phone} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="school-teacher-form-row">
                  <div className="school-teacher-form-group">
                    <label htmlFor="teacher_role">Teacher Role</label>
                    <select id="teacher_role" name="teacher_role" value={teacherFormData.teacher_role} onChange={handleInputChange}>
                      {teacherRoles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="school-teacher-form-group">
                    <label htmlFor="subject">Subject</label>
                    <select id="subject" name="subject" value={teacherFormData.subject} onChange={handleInputChange}>
                      <option value="">Select subject</option>
                      {subjects && subjects.length > 0 ? (
                        subjects.map((subject) => (
                          <option key={subject.id} value={subject.name}>{subject.name}</option>
                        ))
                      ) : (
                        <option value="" disabled>No subjects available</option>
                      )}
                    </select>
                  </div>
                </div>



                <div className="school-teacher-form-group">
                  <label>Grades</label>
                  {grades.length > 0 ? (
                    <div className="school-teacher-grade-checkbox-grid">
                      {buildGradeSectionOptions().map((option) => {
                        const isChecked = selectedGrades.has(option.key);
                        return (
                          <label key={option.key} className={`school-teacher-grade-checkbox ${isChecked ? 'checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleGradeToggle(option.key)}
                            />
                            <span>{option.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: '#666', fontSize: '0.875rem' }}>No grades available. Please add grades for this school first.</p>
                  )}
                </div>

                <div className="school-teacher-form-row">
                  <div className="school-teacher-form-group">
                    <label htmlFor="new_password">Change Password</label>
                    {/* Modern 6-square password UI */}
                    <div className="dashboard-school-teacher-password-squares-container">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <div
                          key={index}
                          className={`dashboard-school-teacher-password-square ${newPassword.length > index ? 'filled' : ''} ${newPassword.length === index ? 'active' : ''}`}
                          onClick={handlePasswordSquareClick}
                        >
                          {newPassword[index] || ''}
                        </div>
                      ))}
                    </div>
                    {/* Hidden input that actually controls the password value */}
                    <input
                      type="password"
                      id="dashboard-school-teacher-password-input"
                      name="new_password"
                      value={newPassword}
                      onChange={handlePasswordChange}
                      className="dashboard-school-teacher-password-hidden-input"
                      placeholder="Enter 6-digit password (numbers only)"
                      maxLength="6"
                      autoComplete="new-password"
                    />
                    <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
                      Password must be 6 digits (numbers only). Current password is shown. Edit to change it, or leave as-is to keep the same password.
                    </small>
                  </div>
                </div>

                <div className="school-teacher-form-divider"></div>

                <div className="school-teacher-form-row">
                  <div className="school-teacher-form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={teacherFormData.is_active === true}
                        onChange={(e) => setTeacherFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                </div>

                <div className="school-teacher-form-actions">
                  <button type="button" className="school-teacher-cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                  <button type="submit" className="school-teacher-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Add Teacher Modal */}
      {!readOnly && showAddTeacherModal && (
        <div className="school-teacher-modal-overlay" onClick={() => handleOverlayClick(closeModal)}>
          <div className="school-teacher-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="school-teacher-modal-header">
              <h2>Add New Teacher</h2>
              <button className="school-teacher-modal-close" onClick={closeModal}>×</button>
            </div>

            {/* Mode Toggle */}
            <div className="school-teacher-add-mode-toggle">
              <button
                className={`school-teacher-mode-toggle-btn ${addMode === 'single' ? 'active' : ''}`}
                onClick={() => setAddMode('single')}
              >
                Single Entry
              </button>
              <button
                className={`school-teacher-mode-toggle-btn ${addMode === 'excel' ? 'active' : ''}`}
                onClick={() => setAddMode('excel')}
              >
                Upload Excel
              </button>
            </div>

            {addMode === 'single' ? (
              <form onSubmit={handleSubmitTeacher} className="school-teacher-form">
                <div className="school-teacher-form-row">
                  <div className="school-teacher-form-group">
                    <label htmlFor="add_first_name">First Name *</label>
                    <input type="text" id="add_first_name" name="first_name" value={teacherFormData.first_name} onChange={handleInputChange} required />
                  </div>
                  <div className="school-teacher-form-group">
                    <label htmlFor="add_last_name">Last Name *</label>
                    <input type="text" id="add_last_name" name="last_name" value={teacherFormData.last_name} onChange={handleInputChange} required />
                  </div>
                </div>

                <div className="school-teacher-form-row">
                  <div className="school-teacher-form-group">
                    <label htmlFor="add_email">Email Address *</label>
                    <input type="email" id="add_email" name="email" value={teacherFormData.email} onChange={handleInputChange} required />
                  </div>
                  <div className="school-teacher-form-group">
                    <label htmlFor="add_phone">Phone Number</label>
                    <input type="tel" id="add_phone" name="phone" value={teacherFormData.phone} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="school-teacher-form-row">
                  <div className="school-teacher-form-group">
                    <label htmlFor="add_teacher_role">Teacher Role</label>
                    <select id="add_teacher_role" name="teacher_role" value={teacherFormData.teacher_role} onChange={handleInputChange}>
                      {teacherRoles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="school-teacher-form-group">
                    <label htmlFor="add_subject">Subject</label>
                    <select id="add_subject" name="subject" value={teacherFormData.subject} onChange={handleInputChange}>
                      <option value="">Select subject</option>
                      {subjects && subjects.length > 0 ? (
                        subjects.map((subject) => (
                          <option key={subject.id} value={subject.name}>{subject.name}</option>
                        ))
                      ) : (
                        <option value="" disabled>No subjects available</option>
                      )}
                    </select>
                  </div>
                </div>



                <div className="school-teacher-form-group">
                  <label>Grades</label>
                  {grades.length > 0 ? (
                    <div className="school-teacher-grade-checkbox-grid">
                      {buildGradeSectionOptions().map((option) => {
                        const isChecked = selectedGrades.has(option.key);
                        return (
                          <label key={option.key} className={`school-teacher-grade-checkbox ${isChecked ? 'checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleGradeToggle(option.key)}
                            />
                            <span>{option.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: '#666', fontSize: '0.875rem' }}>No grades available. Please add grades for this school first.</p>
                  )}
                </div>

                <div className="school-teacher-form-divider"></div>

                <div className="school-teacher-form-row">
                  <div className="school-teacher-form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={teacherFormData.is_active === true}
                        onChange={(e) => setTeacherFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                </div>

                <div className="school-teacher-form-actions">
                  <button type="button" className="school-teacher-cancel-btn" onClick={() => handleOverlayClick(closeModal)}>Cancel</button>
                  <button type="submit" className="school-teacher-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Adding Teacher...' : 'Add Teacher'}</button>
                </div>
              </form>
            ) : (
              <div className="school-teacher-excel-upload-section">
                <div className="school-teacher-excel-upload-info">
                  <h3>Upload Teachers via Excel</h3>
                  <p>Upload an Excel file (.xlsx or .xls) with teacher data. The file should contain the following columns:</p>
                  <ul className="school-teacher-excel-columns-list">
                    <li><strong>first_name</strong> (required)</li>
                    <li><strong>last_name</strong> (required)</li>
                    <li><strong>email</strong> (required)</li>
                    <li><strong>phone</strong> (required)</li>
                    <li><strong>subject</strong> (required)</li>
                  </ul>
                  <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                    <strong>Note:</strong> All teachers will be assigned to the selected grades below. Password will be auto-generated (6 digits) and status will be set to active.
                  </p>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <button
                    type="button"
                    onClick={downloadExcelTemplate}
                    className="school-teacher-submit-btn"
                    style={{ marginBottom: '12px' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Download Excel Template
                  </button>
                </div>

                <div className="school-teacher-form-group" style={{ marginBottom: '16px' }}>
                  <label>Select Grades * (Teachers will teach these grades)</label>
                  {grades.length > 0 ? (
                    <div className="school-teacher-grade-checkbox-grid">
                      {buildGradeSectionOptions().map((option) => {
                        const isChecked = excelSelectedGrades.has(option.key);
                        return (
                          <label key={option.key} className={`school-teacher-grade-checkbox ${isChecked ? 'checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                const newSet = new Set(excelSelectedGrades);
                                if (newSet.has(option.key)) {
                                  newSet.delete(option.key);
                                } else {
                                  // If selecting a subsection, remove the parent grade if it exists
                                  // (can't teach whole grade and specific section at same time)
                                  if (option.key.includes(':')) {
                                    const [gradeId] = option.key.split(':');
                                    newSet.delete(gradeId);
                                  } else {
                                    // If selecting a grade, remove all its subsections
                                    // (can't teach whole grade and specific sections at same time)
                                    Array.from(newSet).forEach(existingKey => {
                                      if (existingKey.startsWith(`${option.key}:`)) {
                                        newSet.delete(existingKey);
                                      }
                                    });
                                  }
                                  newSet.add(option.key);
                                }
                                setExcelSelectedGrades(newSet);
                                setExcelFile(null); // Clear file when grades change
                              }}
                            />
                            <span>{option.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: '#666', fontSize: '0.875rem' }}>No grades available. Please add grades for this school first.</p>
                  )}
                </div>

                <div
                  className={`school-teacher-excel-upload-area ${isDragging ? 'dragging' : ''} ${!canUploadFile() ? 'disabled' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    opacity: canUploadFile() ? 1 : 0.6,
                    pointerEvents: canUploadFile() ? 'auto' : 'none',
                    cursor: canUploadFile() ? 'pointer' : 'not-allowed'
                  }}
                >
                  <input
                    type="file"
                    id="teacher-excel-file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelFileChange}
                    disabled={!canUploadFile()}
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor="teacher-excel-file"
                    className="school-teacher-excel-upload-label"
                    style={{
                      cursor: canUploadFile() ? 'pointer' : 'not-allowed',
                      pointerEvents: canUploadFile() ? 'auto' : 'none'
                    }}
                  >
                    <div className="school-teacher-excel-upload-icon">📁</div>
                    <div className="school-teacher-excel-upload-text">
                      {excelFile
                        ? excelFile.name
                        : canUploadFile()
                          ? (isDragging ? 'Drop Excel file here' : 'Click to select Excel file or drag and drop')
                          : 'Please select at least one grade first'
                      }
                    </div>
                    <small>Supported formats: .xlsx, .xls</small>
                  </label>
                </div>

                <div className="school-teacher-form-actions">
                  <button type="button" className="school-teacher-cancel-btn" onClick={() => handleOverlayClick(closeModal)}>Cancel</button>
                  <button
                    type="button"
                    className="school-teacher-submit-btn"
                    onClick={handleExcelUpload}
                    disabled={!excelFile || !canUploadFile() || isExcelUploading}
                  >
                    {isExcelUploading ? 'Uploading...' : 'Upload Teachers'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="school-teacher-modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="school-teacher-modal-content school-teacher-filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="school-teacher-modal-header">
              <h2>Filter Teachers</h2>
              <button className="school-teacher-modal-close" onClick={() => setShowFilterModal(false)}>×</button>
            </div>

            <div className="school-teacher-filter-content">
              <div className="school-teacher-filter-section">
                <h4>Filter By</h4>
                <div className="school-teacher-filter-options">
                  <label className="school-teacher-filter-option">
                    <input type="radio" name="filterType" value="alphabet" checked={filterType === 'alphabet'} onChange={(e) => setFilterType(e.target.value)} />
                    Alphabetical Order
                  </label>
                  <label className="school-teacher-filter-option">
                    <input type="radio" name="filterType" value="subject" checked={filterType === 'subject'} onChange={(e) => setFilterType(e.target.value)} />
                    Subject
                  </label>
                  <label className="school-teacher-filter-option">
                    <input type="radio" name="filterType" value="grade" checked={filterType === 'grade'} onChange={(e) => setFilterType(e.target.value)} />
                    Grade
                  </label>
                  <label className="school-teacher-filter-option">
                    <input type="radio" name="filterType" value="status" checked={filterType === 'status'} onChange={(e) => setFilterType(e.target.value)} />
                    Status
                  </label>
                  <label className="school-teacher-filter-option">
                    <input type="radio" name="filterType" value="role" checked={filterType === 'role'} onChange={(e) => setFilterType(e.target.value)} />
                    Role
                  </label>
                </div>
              </div>

              {filterType === 'subject' && (
                <div className="school-teacher-filter-section">
                  <h4>Subject</h4>
                  <select className="school-teacher-filter-select" value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
                    <option value="">All Subjects</option>
                    {subjects && subjects.length > 0 ? (
                      subjects.map((subject) => (
                        <option key={subject.id} value={subject.name}>{subject.name}</option>
                      ))
                    ) : (
                      <option value="" disabled>No subjects available</option>
                    )}
                  </select>
                </div>
              )}

              {filterType === 'grade' && (
                <div className="school-teacher-filter-section">
                  <h4>Grade</h4>
                  <select
                    className="school-teacher-filter-select"
                    value={filterGradeId}
                    onChange={(e) => {
                      setFilterGradeId(e.target.value);
                      setFilterGradeSectionId('');
                    }}
                  >
                    <option value="">All Grades</option>
                    {grades && grades.length > 0 ? (
                      grades
                        .filter(grade => grade.is_active)
                        .sort((a, b) => a.order_index - b.order_index)
                        .map(grade => (
                          <option key={grade.id} value={grade.id}>{grade.name}</option>
                        ))
                    ) : (
                      <option value="" disabled>No grades available</option>
                    )}
                  </select>
                </div>
              )}

              {filterType === 'status' && (
                <div className="school-teacher-filter-section">
                  <h4>Status</h4>
                  <select className="school-teacher-filter-select" value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}

              {filterType === 'role' && (
                <div className="school-teacher-filter-section">
                  <h4>Role</h4>
                  <select className="school-teacher-filter-select" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                    <option value="">All Roles</option>
                    {teacherRoles.filter(r => r.value).map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="school-teacher-filter-actions">
                <button className="school-teacher-cancel-btn" onClick={() => {
                  setFilterType('alphabet');
                  setFilterValue('');
                  setFilterGradeId('');
                  setFilterGradeSectionId('');
                  setFilterRole('');
                  setShowFilterModal(false);
                }}>Clear Filters</button>
                <button className="school-teacher-submit-btn" onClick={() => setShowFilterModal(false)}>Apply Filters</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {!readOnly && showDeleteConfirm && itemToDelete && (
        <div className="school-teacher-modal-overlay" onClick={cancelDelete}>
          <div className="school-teacher-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="school-teacher-confirmation-header">
              <div className="school-teacher-confirmation-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
              </div>
              <h2>Delete Teacher</h2>
            </div>

            <div className="school-teacher-confirmation-content">
              <p className="school-teacher-confirmation-message">
                Are you sure you want to delete <strong>"{itemToDelete.data.first_name} {itemToDelete.data.last_name}"</strong>?
              </p>
              <p className="school-teacher-confirmation-warning">
                This action cannot be undone and will permanently remove all associated data.
              </p>
            </div>

            <div className="school-teacher-confirmation-actions">
              <button className="school-teacher-cancel-delete-btn" onClick={cancelDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
                Cancel
              </button>
              <button className="school-teacher-confirm-delete-btn" onClick={confirmDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete Teacher
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {!readOnly && showBulkDeleteConfirm && (
        <div className="bulk-delete-confirmation-overlay">
          <div className="bulk-delete-confirmation-dialog">
            <div className="bulk-delete-confirmation-header">
              <div className="bulk-delete-icon-warning">⚠️</div>
              <h3>Delete {selectedTeachers.size} Teacher{selectedTeachers.size > 1 ? 's' : ''}?</h3>
            </div>

            <div className="bulk-delete-confirmation-body">
              <p>You are about to permanently delete <strong>{selectedTeachers.size}</strong> teacher{selectedTeachers.size > 1 ? 's' : ''}.</p>
              <p className="bulk-delete-warning-text">This action cannot be undone. All data associated with {selectedTeachers.size > 1 ? 'these teachers' : 'this teacher'} will be permanently removed.</p>
            </div>

            <div className="bulk-delete-confirmation-actions">
              <button className="bulk-delete-cancel-btn" onClick={cancelBulkDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
                Cancel
              </button>
              <button className="bulk-delete-confirm-btn" onClick={confirmBulkDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete {selectedTeachers.size} Teacher{selectedTeachers.size > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Results Modal */}
      {showUploadResults && uploadResults && (
        <div className="school-teacher-modal-overlay" onClick={() => setShowUploadResults(false)}>
          <div className="school-teacher-modal-content" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="school-teacher-modal-header">
              <h2>Upload Results</h2>
              <button className="school-teacher-modal-close" onClick={() => setShowUploadResults(false)}>×</button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: '1' }}>
              {/* Summary Section */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                background: uploadResults.success > 0 ? '#f0fdf4' : '#fef2f2',
                border: `2px solid ${uploadResults.success > 0 ? '#22c55e' : '#ef4444'}`,
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: uploadResults.success > 0 ? '#22c55e' : '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white'
                  }}>
                    {uploadResults.success > 0 ? '✓' : '✗'}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
                      {uploadResults.success > 0
                        ? `${uploadResults.success} Teacher${uploadResults.success !== 1 ? 's' : ''} Added Successfully`
                        : 'No Teachers Were Added'
                      }
                    </h3>
                    <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                      {uploadResults.total} total row{uploadResults.total !== 1 ? 's' : ''} processed
                    </p>
                  </div>
                </div>

                {uploadResults.failed.length > 0 && (
                  <div style={{
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <p style={{ margin: 0, color: '#dc2626', fontWeight: '600', fontSize: '14px' }}>
                      {uploadResults.failed.length} Teacher{uploadResults.failed.length !== 1 ? 's' : ''} Could Not Be Added
                    </p>
                  </div>
                )}
              </div>

              {/* Failed Teachers List - Grouped by Reason */}
              {uploadResults.failed.length > 0 && (() => {
                const groupedByReason = uploadResults.failed.reduce((acc, item) => {
                  const reason = item.reason || 'Unknown error';
                  if (!acc[reason]) {
                    acc[reason] = [];
                  }
                  acc[reason].push(item);
                  return acc;
                }, {});

                const reasonGroups = Object.entries(groupedByReason).map(([reason, teachers]) => ({
                  reason,
                  teachers,
                  count: teachers.length
                }));

                return (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>Failed Teachers by Reason</h3>
                      <button
                        onClick={downloadFailedUsersReport}
                        style={{
                          padding: '8px 16px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download Report
                      </button>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      maxHeight: '500px',
                      overflowY: 'auto',
                      paddingRight: '8px'
                    }}>
                      {reasonGroups.map((group, groupIndex) => (
                        <div
                          key={groupIndex}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: 'white'
                          }}
                        >
                          <div style={{
                            padding: '14px 16px',
                            background: '#fef2f2',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: '#ef4444'
                              }}></div>
                              <span style={{
                                fontSize: '15px',
                                fontWeight: '600',
                                color: '#1f2937'
                              }}>
                                {group.reason}
                              </span>
                            </div>
                            <span style={{
                              padding: '4px 12px',
                              background: '#fee2e2',
                              color: '#991b1b',
                              borderRadius: '12px',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}>
                              {group.count} teacher{group.count !== 1 ? 's' : ''}
                            </span>
                          </div>

                          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                              <thead>
                                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Row</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>First Name</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Last Name</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Email</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Phone</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Subject</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.teachers.map((item, index) => (
                                  <tr
                                    key={index}
                                    style={{
                                      borderBottom: index < group.teachers.length - 1 ? '1px solid #f3f4f6' : 'none',
                                      background: index % 2 === 0 ? 'white' : '#f9fafb'
                                    }}
                                  >
                                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{item.row || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.first_name || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.last_name || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.email || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.phone || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.subject || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Success Message */}
              {uploadResults.success > 0 && uploadResults.failed.length === 0 && (
                <div style={{
                  padding: '16px',
                  background: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #22c55e',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0, color: '#166534', fontSize: '15px', fontWeight: '500' }}>
                    🎉 All teachers were added successfully!
                  </p>
                </div>
              )}
            </div>

            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowUploadResults(false);
                  setUploadResults(null);
                  if (uploadResults.success > 0) {
                    closeModal();
                  }
                }}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {uploadResults.success > 0 ? 'Close' : 'Try Again'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete by Excel Modal */}
      {!readOnly && showDeleteByExcelModal && (
        <div className="school-teacher-modal-overlay" onClick={() => setShowDeleteByExcelModal(false)}>
          <div className="school-teacher-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="school-teacher-modal-header">
              <h2>Delete Teachers by Excel</h2>
              <button className="school-teacher-modal-close" onClick={() => {
                setShowDeleteByExcelModal(false);
                setDeleteExcelFile(null);
                setDeleteExcelResults(null);
              }}>×</button>
            </div>

            <div className="school-teacher-excel-upload-section">
              <div className="school-teacher-excel-upload-info">
                <h3>Deactivate Teachers via Excel</h3>
                <p>Upload an Excel file (.xlsx or .xls) with teacher emails. Teachers matching these emails will be marked as inactive.</p>
                <ul className="school-teacher-excel-columns-list">
                  <li><strong>email</strong> (required)</li>
                </ul>
                <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                  <strong>Note:</strong> Teachers will be deactivated (set to inactive status) based on their email addresses. The file can also include first_name, last_name, phone, and subject columns for reference.
                </p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={downloadDeleteExcelTemplate}
                  className="school-teacher-submit-btn"
                  style={{ marginBottom: '12px' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download Excel Template
                </button>
              </div>

              <div
                className={`school-teacher-excel-upload-area ${deleteExcelDragging ? 'dragging' : ''}`}
                onDragOver={handleDeleteExcelDragOver}
                onDragLeave={handleDeleteExcelDragLeave}
                onDrop={handleDeleteExcelDrop}
              >
                <input
                  type="file"
                  id="delete-teacher-excel-file"
                  accept=".xlsx,.xls"
                  onChange={handleDeleteExcelFileChange}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="delete-teacher-excel-file"
                  className="school-teacher-excel-upload-label"
                >
                  <div className="school-teacher-excel-upload-icon">📁</div>
                  <div className="school-teacher-excel-upload-text">
                    {deleteExcelFile
                      ? deleteExcelFile.name
                      : (deleteExcelDragging ? 'Drop Excel file here' : 'Click to select Excel file or drag and drop')
                    }
                  </div>
                  <small>Supported formats: .xlsx, .xls</small>
                </label>
              </div>

              <div className="school-teacher-form-actions">
                <button
                  type="button"
                  className="school-teacher-cancel-btn"
                  onClick={() => {
                    setShowDeleteByExcelModal(false);
                    setDeleteExcelFile(null);
                    setDeleteExcelResults(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="school-teacher-submit-btn"
                  onClick={handleDeleteByExcel}
                  disabled={!deleteExcelFile || isDeleteExcelUploading}
                  style={{
                    background: '#dc3545',
                    borderColor: '#dc3545'
                  }}
                >
                  {isDeleteExcelUploading ? 'Processing...' : 'Deactivate Teachers'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete by Excel Results Modal */}
      {showDeleteExcelResults && deleteExcelResults && (
        <div className="school-teacher-modal-overlay" onClick={() => setShowDeleteExcelResults(false)}>
          <div className="school-teacher-modal-content" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="school-teacher-modal-header">
              <h2>Deactivation Results</h2>
              <button className="school-teacher-modal-close" onClick={() => setShowDeleteExcelResults(false)}>×</button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: '1' }}>
              {/* Summary Section */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                background: deleteExcelResults.success > 0 ? '#f0fdf4' : '#fef2f2',
                border: `2px solid ${deleteExcelResults.success > 0 ? '#22c55e' : '#ef4444'}`,
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: deleteExcelResults.success > 0 ? '#22c55e' : '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white'
                  }}>
                    {deleteExcelResults.success > 0 ? '✓' : '✗'}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
                      {deleteExcelResults.success > 0
                        ? `${deleteExcelResults.success} Teacher${deleteExcelResults.success !== 1 ? 's' : ''} Deactivated Successfully`
                        : 'No Teachers Were Deactivated'
                      }
                    </h3>
                    <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                      {deleteExcelResults.total} total row{deleteExcelResults.total !== 1 ? 's' : ''} processed
                    </p>
                  </div>
                </div>

                {deleteExcelResults.failed.length > 0 && (
                  <div style={{
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <p style={{ margin: 0, color: '#dc2626', fontWeight: '600', fontSize: '14px' }}>
                      {deleteExcelResults.failed.length} Teacher{deleteExcelResults.failed.length !== 1 ? 's' : ''} Could Not Be Deactivated
                    </p>
                  </div>
                )}
              </div>

              {/* Failed Teachers List - Grouped by Reason */}
              {deleteExcelResults.failed.length > 0 && (() => {
                const groupedByReason = deleteExcelResults.failed.reduce((acc, item) => {
                  const reason = item.reason || 'Unknown error';
                  if (!acc[reason]) {
                    acc[reason] = [];
                  }
                  acc[reason].push(item);
                  return acc;
                }, {});

                const reasonGroups = Object.entries(groupedByReason).map(([reason, teachers]) => ({
                  reason,
                  teachers,
                  count: teachers.length
                }));

                return (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>Failed Deactivations by Reason</h3>
                      <button
                        onClick={downloadFailedDeleteReport}
                        style={{
                          padding: '8px 16px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download Report
                      </button>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      maxHeight: '500px',
                      overflowY: 'auto',
                      paddingRight: '8px'
                    }}>
                      {reasonGroups.map((group, groupIndex) => (
                        <div
                          key={groupIndex}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: 'white'
                          }}
                        >
                          <div style={{
                            padding: '14px 16px',
                            background: '#fef2f2',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: '#ef4444'
                              }}></div>
                              <span style={{
                                fontSize: '15px',
                                fontWeight: '600',
                                color: '#1f2937'
                              }}>
                                {group.reason}
                              </span>
                            </div>
                            <span style={{
                              padding: '4px 12px',
                              background: '#fee2e2',
                              color: '#991b1b',
                              borderRadius: '12px',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}>
                              {group.count} teacher{group.count !== 1 ? 's' : ''}
                            </span>
                          </div>

                          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                              <thead>
                                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Row</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>First Name</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Last Name</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Email</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Phone</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Subject</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.teachers.map((item, index) => (
                                  <tr
                                    key={index}
                                    style={{
                                      borderBottom: index < group.teachers.length - 1 ? '1px solid #f3f4f6' : 'none',
                                      background: index % 2 === 0 ? 'white' : '#f9fafb'
                                    }}
                                  >
                                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{item.row || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.first_name || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.last_name || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.email || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.phone || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.subject || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Success Message */}
              {deleteExcelResults.success > 0 && deleteExcelResults.failed.length === 0 && (
                <div style={{
                  padding: '16px',
                  background: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #22c55e',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0, color: '#166534', fontSize: '15px', fontWeight: '500' }}>
                    🎉 All teachers were deactivated successfully!
                  </p>
                </div>
              )}
            </div>

            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowDeleteExcelResults(false);
                  setDeleteExcelResults(null);
                  if (deleteExcelResults.success > 0) {
                    setShowDeleteByExcelModal(false);
                    setDeleteExcelFile(null);
                  }
                }}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {deleteExcelResults.success > 0 ? 'Close' : 'Try Again'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Max Limit Error Overlay */}
      {showMaxLimitError && (
        <div className="max-limit-error-overlay" onClick={() => setShowMaxLimitError(false)}>
          <div className="max-limit-error-content" onClick={(e) => e.stopPropagation()}>
            <div className="max-limit-error-header">
              <div className="max-limit-error-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Maximum Limit Reached</h2>
            </div>
            <div className="max-limit-error-body">
              <p className="max-limit-error-message">
                The maximum number of active {maxLimitError.type} ({maxLimitError.max}) has been reached.
              </p>
              <p className="max-limit-error-details">
                Current active {maxLimitError.type}: {maxLimitError.current} of {maxLimitError.max}
              </p>
              <p className="max-limit-error-note">
                You cannot add more {maxLimitError.type} or change inactive {maxLimitError.type} to active status until the limit is increased or some {maxLimitError.type} are deactivated.
              </p>
            </div>
            <div className="max-limit-error-actions">
              <button className="max-limit-error-close-btn" onClick={() => setShowMaxLimitError(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        autoClose={true}
        autoCloseDelay={4000}
      />
    </div>
  );
};

export default DashboardSchoolTeacher;
